/**
 * Zod mirrors of the vocabulary, for the routes that carry it. They live here
 * rather than in api-contract so the wire and the domain cannot drift: each
 * enum schema is built FROM the const tuple it mirrors, and the `satisfies`
 * checks at the bottom fail to compile if a type and its schema part ways.
 * This is the ops-contract discipline, applied to hardware.
 */

import { z } from 'zod';
import { HARDWARE_CATEGORIES, type HardwareCategory } from './categories.js';
import { TRACKING_TYPES, type TrackingType } from './tracking.js';
import { OWNER_TYPES, type OwnerType } from './ownership.js';
import { ASSET_CONDITIONS, ASSET_STATUSES, type AssetCondition, type AssetStatus } from './asset.js';
import { LOCATION_TYPES, type LocationType } from './location.js';
import { ASSET_ACTOR_TYPES, ASSET_EVENT_TYPES, type AssetActorType, type AssetEventType } from './events.js';
import { KIT_INSTANCE_STATUSES, KIT_READINESS, TRACKING_REQUIREMENTS, type KitInstanceStatus, type KitReadiness, type TrackingRequirement } from './kit.js';
import { ASSIGNMENT_STATUSES, ASSIGNMENT_TYPES, BILLING_MODES, type AssignmentStatus, type AssignmentType, type BillingMode } from './assignment.js';
import { MAINTENANCE_EVENT_TYPES, MAINTENANCE_STATUSES, type MaintenanceEventType, type MaintenanceStatus } from './maintenance.js';
import { INSPECTION_ITEM_OUTCOMES, INSPECTION_RESULTS, type InspectionItemOutcome, type InspectionResult } from './inspection.js';

export const HardwareCategorySchema = z.enum(HARDWARE_CATEGORIES);
export const TrackingTypeSchema = z.enum(TRACKING_TYPES);
export const OwnerTypeSchema = z.enum(OWNER_TYPES);
export const AssetStatusSchema = z.enum(ASSET_STATUSES);
export const AssetConditionSchema = z.enum(ASSET_CONDITIONS);
export const LocationTypeSchema = z.enum(LOCATION_TYPES);
export const AssetEventTypeSchema = z.enum(ASSET_EVENT_TYPES);
export const AssetActorTypeSchema = z.enum(ASSET_ACTOR_TYPES);
export const KitInstanceStatusSchema = z.enum(KIT_INSTANCE_STATUSES);
export const KitReadinessSchema = z.enum(KIT_READINESS);
export const TrackingRequirementSchema = z.enum(TRACKING_REQUIREMENTS);
export const AssignmentTypeSchema = z.enum(ASSIGNMENT_TYPES);
export const AssignmentStatusSchema = z.enum(ASSIGNMENT_STATUSES);
export const BillingModeSchema = z.enum(BILLING_MODES);
export const MaintenanceEventTypeSchema = z.enum(MAINTENANCE_EVENT_TYPES);
export const MaintenanceStatusSchema = z.enum(MAINTENANCE_STATUSES);
export const InspectionResultSchema = z.enum(INSPECTION_RESULTS);
export const InspectionItemOutcomeSchema = z.enum(INSPECTION_ITEM_OUTCOMES);

// Parity: the types and their schemas describe the same closed set, or this
// fails to compile.
type SchemaOf<T extends z.ZodTypeAny> = z.infer<T>;
const _cat: HardwareCategory = 'CABLE' satisfies SchemaOf<typeof HardwareCategorySchema>;
const _trk: TrackingType = 'SERIALIZED' satisfies SchemaOf<typeof TrackingTypeSchema>;
const _own: OwnerType = 'HIPNUMBER' satisfies SchemaOf<typeof OwnerTypeSchema>;
const _st: AssetStatus = 'IN_STOCK' satisfies SchemaOf<typeof AssetStatusSchema>;
const _cond: AssetCondition = 'GOOD' satisfies SchemaOf<typeof AssetConditionSchema>;
const _loc: LocationType = 'HIPNUMBER_WAREHOUSE' satisfies SchemaOf<typeof LocationTypeSchema>;
const _ev: AssetEventType = 'CREATED' satisfies SchemaOf<typeof AssetEventTypeSchema>;
const _act: AssetActorType = 'OPERATOR' satisfies SchemaOf<typeof AssetActorTypeSchema>;
const _ks: KitInstanceStatus = 'ASSEMBLING' satisfies SchemaOf<typeof KitInstanceStatusSchema>;
const _kr: KitReadiness = 'READY' satisfies SchemaOf<typeof KitReadinessSchema>;
const _tr: TrackingRequirement = 'SERIALIZED' satisfies SchemaOf<typeof TrackingRequirementSchema>;
const _at: AssignmentType = 'RENTAL' satisfies SchemaOf<typeof AssignmentTypeSchema>;
const _as: AssignmentStatus = 'ACTIVE' satisfies SchemaOf<typeof AssignmentStatusSchema>;
const _bm: BillingMode = 'NONE' satisfies SchemaOf<typeof BillingModeSchema>;
const _me: MaintenanceEventType = 'REPAIR' satisfies SchemaOf<typeof MaintenanceEventTypeSchema>;
const _ms: MaintenanceStatus = 'OPEN' satisfies SchemaOf<typeof MaintenanceStatusSchema>;
const _ir: InspectionResult = 'READY' satisfies SchemaOf<typeof InspectionResultSchema>;
const _io: InspectionItemOutcome = 'PASS' satisfies SchemaOf<typeof InspectionItemOutcomeSchema>;
void _cat; void _trk; void _own; void _st; void _cond; void _loc; void _ev; void _act; void _ks; void _kr; void _tr; void _at; void _as; void _bm; void _me; void _ms; void _ir; void _io;
